// src/pages/collaborators/RegisterCollaborator.jsx
// Page d'inscription gratuite pour les collaborateurs invités via token.
// Flux : token → créer compte Supabase → accept_collaborator_invitation → /dashboard
// Aucun abonnement requis — le compte est satellite de l'invitant.

import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";

export default function RegisterCollaborator() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");

  // États
  const [step, setStep] = useState("loading"); // loading | invalid | form | submitting | done | error
  const [inviteInfo, setInviteInfo] = useState(null); // { email, first_name, inviter_name, role }
  const [form, setForm] = useState({ email: "", password: "", confirmPassword: "", first_name: "", last_name: "" });
  const [errorMsg, setErrorMsg] = useState("");
  const [alreadyHasAccount, setAlreadyHasAccount] = useState(false);

  // ── 1. Charger les infos de l'invitation depuis le token ──
  useEffect(() => {
    if (!token) { setStep("invalid"); return; }

    async function loadInvite() {
      // ✅ RPC SECURITY DEFINER — contourne RLS, accessible sans auth
      const { data, error } = await supabase.rpc("get_collaborator_invite_info", {
        p_token: token,
      });

      if (error) {
        console.error("[RegisterCollaborator] RPC error:", error);
        setStep("invalid");
        return;
      }

      if (!data?.valid) {
        const reason = data?.reason;
        if (reason === "active") {
          // Invitation déjà acceptée → rediriger vers login
          navigate(`/login?redirect=${encodeURIComponent("/collaborators/accept?token=" + token)}`, { replace: true });
          return;
        }
        setStep("invalid");
        return;
      }

      setInviteInfo({
        email:       data.email,
        first_name:  data.first_name,
        last_name:   data.last_name,
        role:        data.role,
        inviterName: data.inviter_name,
      });
      setForm(f => ({
        ...f,
        email:      data.email      || "",
        first_name: data.first_name || "",
        last_name:  data.last_name  || "",
      }));
      setStep("form");
    }

    loadInvite();
  }, [token, navigate]);

  // ── 2. Vérifier si le user est déjà connecté ──
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session && step === "form") {
        // Déjà connecté → accepter directement
        acceptInvitation(session);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const acceptInvitation = async (session) => {
    setStep("submitting");
    const { error } = await supabase.rpc("accept_collaborator_invitation", { p_token: token });
    if (error) {
      const msg = (error.message || "").toLowerCase();
      if (msg.includes("email mismatch")) {
        setErrorMsg("Cet email ne correspond pas à l'invitation. Connectez-vous avec l'adresse email invitée.");
      } else if (msg.includes("invalid token") || msg.includes("not pending")) {
        setErrorMsg("Invitation invalide ou déjà utilisée.");
      } else {
        setErrorMsg(error.message || "Impossible d'accepter l'invitation.");
      }
      setStep("error");
      return;
    }
    setStep("done");
    setTimeout(() => navigate("/delegated", { replace: true }), 1800);
  };

  // ── 3. Soumettre le formulaire : créer compte puis accepter ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (form.password !== form.confirmPassword) {
      setErrorMsg("Les mots de passe ne correspondent pas.");
      return;
    }
    if (form.password.length < 6) {
      setErrorMsg("Le mot de passe doit faire au moins 6 caractères.");
      return;
    }

    setStep("submitting");

    // Créer le compte Supabase Auth
    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: {
        data: {
          first_name: form.first_name,
          last_name: form.last_name,
          is_collaborator: true,      // flag satellite — pas d'abonnement requis
          invited_by_token: token,
        }
      }
    });

    if (signUpError) {
      // Peut-être l'email existe déjà
      if (signUpError.message?.toLowerCase().includes("already")) {
        setAlreadyHasAccount(true);
        setErrorMsg("Un compte existe déjà avec cet email. Connectez-vous pour accepter l'invitation.");
        setStep("form");
        return;
      }
      setErrorMsg(signUpError.message || "Erreur lors de la création du compte.");
      setStep("form");
      return;
    }

    // Si email non confirmé (selon config Supabase), on peut quand même avoir une session
    const session = signUpData?.session;
    if (session) {
      await acceptInvitation(session);
    } else {
      // Email de confirmation requis — on informe et on stocke le token pour après confirmation
      localStorage.setItem("artkiv_pending_collab_token", token);
      setStep("confirm_email");
    }
  };

  // ── Rendu ──

  const roleLabel = {
    team_member: "Membre de l'équipe",
    team_readonly: "Accès lecture seule",
    expert: "Expert invité",
    assistant: "Assistant",
    client: "Client",
  }[inviteInfo?.role] || "Collaborateur";

  const containerStyle = {
    minHeight: "100vh",
    background: "#fafaf8",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 16px",
    fontFamily: "system-ui, -apple-system, sans-serif",
  };

  const cardStyle = {
    background: "#fff",
    border: "1px solid #e5e3df",
    padding: "40px 40px 36px",
    maxWidth: 480,
    width: "100%",
  };

  const labelStyle = {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#4a4a4a",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    marginBottom: 6,
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #e5e3df",
    borderRadius: 1,
    fontSize: 14,
    color: "#1a1a1a",
    background: "#fafaf8",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "system-ui, sans-serif",
  };

  const btnPrimary = {
    width: "100%",
    padding: "13px 0",
    background: "#1a1a1a",
    color: "#fff",
    border: "none",
    borderRadius: 1,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    letterSpacing: "0.04em",
    fontFamily: "system-ui, sans-serif",
    marginTop: 8,
  };

  // ── États de rendu ──

  if (step === "loading") {
    return (
      <div style={containerStyle}>
        <div style={{ fontFamily: "Georgia, serif", color: "#6b6b6b", fontSize: 16 }}>
          Validation de votre invitation…
        </div>
      </div>
    );
  }

  if (step === "invalid") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a1a1a", marginBottom: 12 }}>
            Invitation invalide
          </div>
          <p style={{ fontSize: 14, color: "#6b6b6b", lineHeight: 1.7, marginBottom: 24 }}>
            Ce lien d'invitation est invalide, expiré ou a déjà été utilisé. Demandez à votre expert de renvoyer l'invitation.
          </p>
          <Link to="/login" style={{ fontSize: 13, color: "#c9a961" }}>→ Se connecter</Link>
        </div>
      </div>
    );
  }

  if (step === "submitting") {
    return (
      <div style={containerStyle}>
        <div style={{ fontFamily: "Georgia, serif", color: "#6b6b6b", fontSize: 16 }}>
          Création de votre accès en cours…
        </div>
      </div>
    );
  }

  if (step === "done") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ fontSize: 28, marginBottom: 16 }}>🗝️</div>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a1a1a", marginBottom: 10 }}>
            Accès activé
          </div>
          <p style={{ fontSize: 14, color: "#6b6b6b", lineHeight: 1.7 }}>
            Bienvenue. Vous avez maintenant accès aux collections partagées. Redirection en cours…
          </p>
        </div>
      </div>
    );
  }

  if (step === "confirm_email") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a1a1a", marginBottom: 12 }}>
            Confirmez votre email
          </div>
          <p style={{ fontSize: 14, color: "#6b6b6b", lineHeight: 1.7, marginBottom: 16 }}>
            Un email de confirmation a été envoyé à <strong>{form.email}</strong>. Cliquez sur le lien dans cet email pour activer votre accès collaborateur.
          </p>
          <p style={{ fontSize: 13, color: "#9a8c7e", lineHeight: 1.6 }}>
            Une fois confirmé, connectez-vous et votre accès sera automatiquement activé.
          </p>
        </div>
      </div>
    );
  }

  if (step === "error") {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ fontFamily: "Georgia, serif", fontSize: 20, color: "#1a1a1a", marginBottom: 12 }}>
            Une erreur est survenue
          </div>
          <p style={{ fontSize: 14, color: "#b00020", marginBottom: 20 }}>{errorMsg}</p>
          <button onClick={() => setStep("form")} style={{ ...btnPrimary, width: "auto", padding: "10px 20px" }}>
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // ── Formulaire principal ──
  return (
    <div style={containerStyle}>
      {/* Logo */}
      <div style={{ marginBottom: 28, textAlign: "center" }}>
        <Link to="/" style={{ textDecoration: "none" }}>
          <span style={{ fontFamily: "Georgia, serif", fontSize: 20, color: "#1a1a1a", letterSpacing: "0.12em", fontWeight: 400 }}>
            ARTKIV
          </span>
        </Link>
      </div>

      <div style={cardStyle}>
        {/* Badge accès gratuit */}
        <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "4px 10px", background: "#f0faf0", color: "#065f46", border: "1px solid #d1fae5" }}>
            ✓ Accès inclus — aucun abonnement requis
          </span>
          <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", padding: "4px 10px", background: "#f9f7f4", color: "#6b6b6b", border: "1px solid #e5e3df" }}>
            {roleLabel}
          </span>
        </div>

        <h1 style={{ fontFamily: "Georgia, serif", fontWeight: 400, fontSize: 24, color: "#1a1a1a", margin: "0 0 8px" }}>
          Rejoindre l'équipe
        </h1>
        <p style={{ fontSize: 14, color: "#6b6b6b", lineHeight: 1.6, margin: "0 0 28px" }}>
          <strong>{inviteInfo?.inviterName}</strong> vous invite à collaborer sur Artkiv.
          Créez votre accès gratuit — votre compte est pris en charge dans son abonnement.
        </p>

        {/* Déjà un compte ? */}
        {alreadyHasAccount && (
          <div style={{ padding: "12px 16px", background: "#fef3c7", border: "1px solid #fcd34d", marginBottom: 20, fontSize: 13, color: "#92400e", lineHeight: 1.6 }}>
            Un compte existe déjà avec cette adresse.{" "}
            <Link
              to={`/login?redirect=${encodeURIComponent(`/collaborators/accept?token=${token}`)}`}
              style={{ color: "#92400e", fontWeight: 600, textDecoration: "underline" }}
            >
              Connectez-vous pour accepter l'invitation →
            </Link>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Nom / Prénom */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>Prénom</label>
              <input
                style={inputStyle}
                type="text"
                value={form.first_name}
                onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                placeholder="Prénom"
                autoComplete="given-name"
              />
            </div>
            <div>
              <label style={labelStyle}>Nom</label>
              <input
                style={inputStyle}
                type="text"
                value={form.last_name}
                onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                placeholder="Nom"
                autoComplete="family-name"
              />
            </div>
          </div>

          {/* Email — pré-rempli et verrouillé si l'invitation a un email précis */}
          <div>
            <label style={labelStyle}>Adresse email *</label>
            <input
              style={{ ...inputStyle, background: inviteInfo?.email ? "#f5f5f5" : "#fafaf8", color: inviteInfo?.email ? "#6b6b6b" : "#1a1a1a" }}
              type="email"
              value={form.email}
              onChange={e => !inviteInfo?.email && setForm(f => ({ ...f, email: e.target.value }))}
              readOnly={!!inviteInfo?.email}
              required
              placeholder="votre@email.com"
              autoComplete="email"
            />
            {inviteInfo?.email && (
              <p style={{ fontSize: 11, color: "#9a8c7e", marginTop: 4 }}>
                Adresse définie par votre expert. Connectez-vous avec cet email.
              </p>
            )}
          </div>

          {/* Mot de passe */}
          <div>
            <label style={labelStyle}>Mot de passe *</label>
            <input
              style={inputStyle}
              type="password"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              required
              placeholder="Minimum 6 caractères"
              autoComplete="new-password"
            />
          </div>

          <div>
            <label style={labelStyle}>Confirmer le mot de passe *</label>
            <input
              style={inputStyle}
              type="password"
              value={form.confirmPassword}
              onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))}
              required
              placeholder="Répétez le mot de passe"
              autoComplete="new-password"
            />
          </div>

          {/* Erreur */}
          {errorMsg && !alreadyHasAccount && (
            <div style={{ padding: "10px 14px", background: "#fef2f2", border: "1px solid #fecaca", fontSize: 13, color: "#b00020", lineHeight: 1.5 }}>
              {errorMsg}
            </div>
          )}

          {/* Disclaimer */}
          <p style={{ fontSize: 11, color: "#9a8c7e", lineHeight: 1.6, margin: "0" }}>
            En créant votre accès, vous acceptez les{" "}
            <Link to="/legal" style={{ color: "#9a8c7e" }}>Mentions légales</Link>{" "}
            et la{" "}
            <Link to="/privacy-policy" style={{ color: "#9a8c7e" }}>Politique de confidentialité</Link>{" "}
            d'Artkiv.
          </p>

          <button type="submit" style={btnPrimary}>
            Créer mon accès collaborateur
          </button>
        </form>

        {/* Lien connexion */}
        <p style={{ textAlign: "center", marginTop: 20, fontSize: 13, color: "#6b6b6b" }}>
          Déjà un compte ?{" "}
          <Link
            to={`/login?redirect=${encodeURIComponent(`/collaborators/accept?token=${token}`)}`}
            style={{ color: "#1a1a1a", fontWeight: 600 }}
          >
            Se connecter →
          </Link>
        </p>
      </div>
    </div>
  );
}